/**
 * Recent Transactions Component
 * 
 * Displays the most recent financial transactions across all types.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ArrowUpRight,
  Receipt,
  Wallet,
  Clock,
  Loader2,
  History,
  Search,
  X,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CurrencyDisplay } from './CurrencyDisplay';
import { cn } from '@/lib/utils';
import { translateRevenueType, translateExpenseType, translateDailyCategory } from '@/lib/translations';
import { dashboardService } from '@/services/financial.service';
import { Link } from 'react-router-dom';
import { toBrasiliaWallClock } from '@/lib/brasiliaDate';

interface RecentTransactionsProps {
  limit?: number;
  showSearch?: boolean;
  className?: string;
  periodId?: string;
}

const transactionTypeConfig: Record<string, {
  label: string;
  icon: typeof ArrowUpRight;
  color: string;
  bgColor: string;
  link: string;
}> = {
  revenue: {
    label: 'Receita',
    icon: ArrowUpRight,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950/30',
    link: '/financial/revenues',
  },
  monthly_expense: {
    label: 'Despesa Mensal',
    icon: Receipt,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50 dark:bg-rose-950/30',
    link: '/financial/monthly-expenses',
  },
  daily_expense: {
    label: 'Despesa Diária',
    icon: Wallet,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50 dark:bg-amber-950/30',
    link: '/financial/daily-expenses',
  },
};

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending: { label: 'Pendente', variant: 'outline' },
  realized: { label: 'Realizado', variant: 'default' },
  partially_realized: { label: 'Parcial', variant: 'secondary' },
  active: { label: 'Ativo', variant: 'default' },
};

export function RecentTransactions({
  limit = 10,
  showSearch = true,
  className,
  periodId,
}: Readonly<RecentTransactionsProps>) {
  const [searchTerm, setSearchTerm] = useState('');
  
  const { data: transactions, isLoading } = useQuery({
    queryKey: ['recent-transactions', limit, periodId],
    queryFn: () => dashboardService.getRecentTransactions(limit, periodId),
    staleTime: 30 * 1000, // 30 seconds
  });
  
  const filteredTransactions = transactions?.filter((t) =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.type.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];
  
  return (
    <Card className={cn('border-border/60', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            <CardTitle className="text-sm font-normal uppercase leading-[18px]">ÚLTIMOS LANÇAMENTOS</CardTitle>
          </div>
          <Badge variant="secondary" className="text-xs">
            {transactions?.length || 0} recentes
          </Badge>
        </div>
        <CardDescription>
          Seus lançamentos financeiros mais recentes
        </CardDescription>
      </CardHeader>
      <CardContent>
        {showSearch && (
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Filtrar lançamentos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-9"
            />
            {searchTerm.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setSearchTerm('')}
                aria-label="Limpar filtro de lançamentos"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
        
        {(() => {
          if (isLoading) return (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
          );
          if (filteredTransactions.length === 0) return (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Clock className="h-10 w-10 text-muted-foreground/30 mb-2" />
                <p className="text-sm font-medium">Nenhum lançamento encontrado</p>
                <p className="text-xs mt-1">
                  {searchTerm ? 'Tente outro termo de busca' : 'Seus lançamentos aparecerão aqui'}
                </p>
              </div>
          );
          return (
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-2">
                  {filteredTransactions.map((transaction) => {
                    const config = transactionTypeConfig[transaction.type];
                    const status = statusConfig[transaction.status] || statusConfig.pending;
                    const Icon = config?.icon || Clock;
                    // Data pura (realizada) fica como está; o created_at (UTC sem
                    // fuso) de lançamentos pendentes vira o dia de Brasília.
                    const parsedDate = toBrasiliaWallClock(transaction.created_at);

                    return (
                      <div
                        key={transaction.id}
                        className="group flex items-center justify-between p-3 rounded-lg border border-border/40 hover:border-border hover:bg-muted/30 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn('p-2 rounded-lg', config?.bgColor)}>
                            <Icon className={cn('h-4 w-4', config?.color)} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-sm truncate max-w-[180px]">
                                {transaction.name}
                              </h4>
                              <Badge variant={status.variant} className="text-[10px] px-1.5 py-0">
                                {status.label}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                              <span>{config?.label}</span>
                              {transaction.subtype && (
                                <>
                                  <span>•</span>
                                  <span className="capitalize">
                                    {transaction.type === 'revenue' 
                                      ? translateRevenueType(transaction.subtype)
                                      : transaction.type === 'daily_expense'
                                      ? translateDailyCategory(transaction.subtype)
                                      : translateExpenseType(transaction.subtype)
                                    }
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                    
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <CurrencyDisplay
                              value={transaction.realized || 0}
                              size="sm"
                              className="font-medium"
                            />
                            {transaction.planned > 0 && transaction.realized !== transaction.planned && (
                              <div className="text-[10px] text-muted-foreground">
                                de <CurrencyDisplay value={transaction.planned} size="xs" />
                              </div>
                            )}
                          </div>
                          {parsedDate && (
                            <div className="text-[10px] text-muted-foreground min-w-[60px] text-right">
                              {format(parsedDate, "dd MMM", { locale: ptBR })}
                            </div>
                          )}
                          <Link to={config?.link || '#'}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
          );
        })()}
      </CardContent>
    </Card>
  );
}

export default RecentTransactions;
